import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi } from './managementApi';

const MAX_CONSECUTIVE_FAILURES = 10;
const MAX_LOGGED_CHANGES = 500;
const MAX_LOG_ENTRIES = 300;
// The server waits for Freshdesk for at most two minutes per request, a request that takes longer than this is given up
const REQUEST_TIMEOUT_MS = 150000;
const COOKIE_NAME = 'bftFreshdeskTagging';
const COOKIE_DAYS = 90;

const counts = { notFound: 0, unchanged: 0, withOrderNumbers: 0, changed: 0 };
const initialState = {
    status: 'idle',
    from: null,
    to: null,
    write: false,
    // The last ticket that has been gone through in this run, for the progress
    lastDone: null,
    // The ticket that a stopped run is continued after. It stays before the first ticket that failed, so a failed ticket is run again and not skipped.
    resumeAfter: null,
    // The position that is saved in the cookie, { from, to, lastDone }. Only a run that writes changes it, a dry run has not processed anything.
    saved: null,
    // The ticket that is being worked on and when it was started, to see which ticket a run is stuck on
    currentTicket: null,
    currentStartedAt: null,
    // Tickets done since this session was started, for the speed
    sessionDone: 0,
    ...counts,
    changes: [],
    // Every ticket that is done, newest first
    log: [],
    errors: [],
    error: null,
    startedAt: null,
};

const readCookie = () => {
    const prefix = `${COOKIE_NAME}=`;
    const cookie = document.cookie.split('; ').find((part) => part.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.substring(prefix.length)) : null;
};

// How far the last run that wrote tags got (the range and the last ticket that is done) is kept in a cookie so that it is still there after a reload, a crash or a restart
const loadSaved = () => {
    try {
        const saved = JSON.parse(readCookie());
        return saved && saved.from > 0 && saved.to >= saved.from ? { from: saved.from, to: saved.to, lastDone: saved.lastDone ?? null } : null;
    } catch {
        return null;
    }
};

// Written after every ticket that a run has written. A few tickets can be lost if the computer stops at once, they are then run again,
// which is harmless since a ticket that is done is not written twice.
const save = (position) => {
    try {
        const expires = new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(position))}; expires=${expires}; path=/; SameSite=Lax${secure}`;
    } catch {
        // Not being able to remember where the run is does not stop the run
    }
};

const withSaved = () => ({ ...initialState, saved: loadSaved() });

// Adds a line to the log, newest first
const addLog = (prev, entry) => [{ ...entry, at: Date.now() }, ...prev.log].slice(0, MAX_LOG_ENTRIES);

// Adds the result of one ticket to the counts, the log and the list of tickets that were (or would be) changed
const applyResult = (prev, result, write) => {
    if (result.status === 'notFound') {
        return { ...prev, notFound: prev.notFound + 1, log: addLog(prev, { ticketId: result.ticketId, kind: 'notFound' }) };
    }

    const next = { ...prev, withOrderNumbers: prev.withOrderNumbers + (result.orderNumbers?.length > 0 ? 1 : 0) };
    // The tags are the ones the ticket has in Freshdesk now, also when nothing is changed
    const entry = { ticketId: result.ticketId, url: result.url, orderNumbers: result.orderNumbers ?? [], tags: result.tags ?? [] };
    if (result.status === 'unchanged') {
        return { ...next, unchanged: prev.unchanged + 1, log: addLog(prev, { ...entry, kind: 'unchanged' }) };
    }

    const change = { ...entry, added: result.added, removed: result.removed, written: write };
    return {
        ...next,
        changed: prev.changed + 1,
        changes: [change, ...prev.changes].slice(0, MAX_LOGGED_CHANGES),
        log: addLog(prev, { ...change, kind: write ? 'written' : 'wouldWrite' }),
    };
};

// Goes through a range of Freshdesk tickets from the client, one ticket at a time, so that progress can be shown and no request ever times out.
// It is a dry run that only reads unless write is true. Only the tags of the tickets are written.
// status is one of: idle, running, succeeded, failed, cancelled
const useFreshdeskTagging = () => {
    const [state, setState] = useState(withSaved);
    const stopped = useRef(false);
    const unmounted = useRef(false);
    const request = useRef(null);

    useEffect(() => {
        unmounted.current = false;
        return () => {
            unmounted.current = true;
            request.current?.abort();
        };
    }, []);

    const update = useCallback((changes) => {
        if (!unmounted.current) setState((prev) => ({ ...prev, ...changes }));
    }, []);

    // Also cancels the request that is running, so that a ticket that hangs does not keep the run from stopping.
    // The ticket is then not counted as done, it is run again when the run is continued.
    const stop = useCallback(() => {
        stopped.current = true;
        request.current?.abort();
    }, []);

    // from and to are the whole range. A fresh run starts at from. A run that is continued starts at the ticket after lastDone: the one of this session with the counts kept,
    // or the one that was saved in the cookie, given as continueAfter, with new counts.
    // The cookie is only written by a run that writes tags. A dry run has not processed anything, so it leaves the saved position alone.
    const run = useCallback(async (from, to, write, resume = false, continueAfter = null) => {
        stopped.current = false;

        const keepCounts = resume && continueAfter === null;
        let lastDone = resume ? (continueAfter ?? state.resumeAfter) : null;
        let resumeAfter = lastDone;
        const first = lastDone ? lastDone + 1 : from;
        let firstFailed = null;

        setState((prev) => ({
            ...(keepCounts ? prev : { ...initialState, saved: prev.saved }),
            status: 'running',
            from,
            to,
            write,
            lastDone,
            resumeAfter,
            error: null,
            sessionDone: 0,
            startedAt: Date.now(),
        }));

        // Remembers how far a run that writes has got, in the cookie and on the screen
        const remember = (position) => {
            if (!write) return;
            save(position);
            update({ saved: position });
        };

        let failureStreak = 0;
        let streakStart = null;

        for (let ticketId = first; ticketId <= to; ticketId++) {
            if (stopped.current || unmounted.current) {
                update({ status: 'cancelled', currentTicket: null, currentStartedAt: null });
                return;
            }

            update({ currentTicket: ticketId, currentStartedAt: Date.now() });

            const controller = new AbortController();
            request.current = controller;
            let timedOut = false;
            const timer = setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, REQUEST_TIMEOUT_MS);

            try {
                const result = await callApi('freshdesk/order-tags', { ticketId, write }, controller);
                if (!result) throw new Error('Tomt svar från servern');
                failureStreak = 0;
                streakStart = null;
                setState((prev) => applyResult(prev, result, write));
            } catch (err) {
                // Stopped by the user while the request was running
                if (stopped.current || unmounted.current) {
                    update({ status: 'cancelled', currentTicket: null, currentStartedAt: null });
                    return;
                }

                const message = timedOut ? `Tog längre än ${REQUEST_TIMEOUT_MS / 1000} sekunder, avbröts` : err.message;
                if (streakStart === null) streakStart = ticketId;
                if (firstFailed === null) firstFailed = ticketId;
                failureStreak += 1;
                setState((prev) => ({
                    ...prev,
                    errors: [...prev.errors, { ticketId, error: message }],
                    log: addLog(prev, { ticketId, kind: 'error', error: message }),
                }));

                // Stop instead of failing every remaining ticket when Freshdesk or the server cannot be reached. Continue from the first failed ticket.
                if (failureStreak >= MAX_CONSECUTIVE_FAILURES) {
                    lastDone = streakStart - 1;
                    resumeAfter = firstFailed - 1;
                    remember({ from, to, lastDone: resumeAfter });
                    update({
                        status: 'failed',
                        currentTicket: null,
                        currentStartedAt: null,
                        lastDone,
                        resumeAfter,
                        error: `Avbröt efter ${MAX_CONSECUTIVE_FAILURES} misslyckade anrop i rad, senast ärende ${ticketId}: ${message}`,
                    });
                    return;
                }
            } finally {
                clearTimeout(timer);
                request.current = null;
            }

            lastDone = ticketId;
            resumeAfter = firstFailed === null ? ticketId : firstFailed - 1;
            remember({ from, to, lastDone: resumeAfter });
            setState((prev) => ({ ...prev, lastDone, resumeAfter, sessionDone: prev.sessionDone + 1 }));
        }

        update({ status: 'succeeded', currentTicket: null, currentStartedAt: null });
    }, [state.resumeAfter, update]);

    return { ...state, running: state.status === 'running', run, stop };
};

export default useFreshdeskTagging;
